'use client';

import type { Collaborator } from '@/app/models/collaborator';
import { ExtraCollaboratorResponse } from '@/app/models/extraCollaborator';
import { useListAllCollaboratorQuery } from '@/app/services/extraCollaboratorApi';
import { useSearchPersonQuery } from '@/app/services/personApi';
import GenericAutoComplete from '@/components/common/GenericAutoComplete';
import LateralDrawer from '@/components/common/LateralDrawer';
import PageTitle from '@/components/common/pageTitle';
import TextButton from '@/components/common/TextButton';
import ExtraCollaboratorTable from '@/components/extraCollaborator/ExtraCollaboratorTable';
import NewEditExtraCollaborator from '@/components/extraCollaborator/newEditExtraCollaborator';
import { Box, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function ExtraCollaborator() {
  const theme = useTheme();
  const { t } = useTranslation(['extraCollaborator', 'common']);
  const [drawerStatus, setDrawerStatus] = useState(false);

  const [collaborator, setCollaborator] = useState<Collaborator | null>(null);
  const [collaboratorSearch, setCollaboratorSearch] = useState('');
  const { data: collaborators, isFetching: isLoadingCollaborators } = useSearchPersonQuery(
    {
      searchTerm: collaboratorSearch,
      available: true,
      page: 0,
      size: 10,
      sortKey: 'name',
      sortDirection: 'ASC',
    },
    { skip: collaboratorSearch.length < 1 },
  );

  const [selectedCollaborator, setSelectedCollaborator] = useState<ExtraCollaboratorResponse | null>(null);
  const { data } = useListAllCollaboratorQuery({
    searchTerm: (collaborator?.badge as string) || '',
    page: 0,
    size: 10,
  });

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        mr: 2,
      }}
    >
      <PageTitle
        title={t('historic.extra.collaborator')}
        titleLabel={` (${t('historic.post.allocation')})`}
        subtitle={t('historic.extra.collaborator.subtitle') as string}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          mb: 3,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ flex: 1, minWidth: theme.spacing(31) }}>
          <Typography component="p" variant="fieldHeader">
            {t('common:collaborator')}
          </Typography>
          <GenericAutoComplete
            value={collaborator}
            onChange={(value) => {
              setCollaborator(value);
            }}
            equalityTest={(option, value) => option?.email === value?.email}
            getOptionLabel={(option) => `${option?.badge} - ${option?.name}`}
            onSearch={(value) => {
              setCollaboratorSearch(value);
            }}
            options={collaboratorSearch !== '' && collaborators?.content ? collaborators?.content : []}
            placeholder={t('historic.input.search.collaborator') || ''}
            loading={isLoadingCollaborators}
            sx={{ width: theme.spacing(50) }}
          />
        </Box>
        <TextButton
          sx={{ ml: 'auto' }}
          text={`${t('historic.new.config')}`}
          onClick={() => {
            setDrawerStatus(true);
          }}
        />
      </Box>
      <ExtraCollaboratorTable
        rows={data?.content || []}
        setSelectedItem={setSelectedCollaborator}
        setDrawerOpen={setDrawerStatus}
      />
      <LateralDrawer open={drawerStatus} setOpen={setDrawerStatus} position="right" size="bigger">
        <NewEditExtraCollaborator setDrawerOpen={setDrawerStatus} collaboratorSelect={selectedCollaborator || null} />
      </LateralDrawer>
    </Box>
  );
}
